import { tooltipClasses } from "@mui/material"
import { createTheme, alpha } from "@mui/material/styles"
import createBreakpoints from "@mui/system/createTheme/createBreakpoints"
import {
  teal,
  lightBlue,
  lightGreen,
  orange,
  red,
  blueGrey,
  indigo
} from "./palette"
import {
  FOCUS_COLOR,
  FOCUS_COLOR_ON_DARK,
  focusRing,
  focusRingOnDark,
  focusRingTwoTone
} from "./focusStyles"

export interface SubjectColorOptions {
  primary: string
  secondary: string
}
declare module "@mui/material/styles/createPalette" {
  interface Subjects {
    // neutral: Palette['primary'];
    math: SubjectColorOptions
    mathematics: SubjectColorOptions
    science: SubjectColorOptions
    english: SubjectColorOptions
    englishi: SubjectColorOptions
    englishii: SubjectColorOptions
    englishiii: SubjectColorOptions
    reading: SubjectColorOptions
    overall: SubjectColorOptions
    writingandlanguage: SubjectColorOptions
    readingandwriting: SubjectColorOptions
    algebra: SubjectColorOptions
    algebrai: SubjectColorOptions
    algebraii: SubjectColorOptions
    biology: SubjectColorOptions
    ushistory: SubjectColorOptions
    elar: SubjectColorOptions
  }

  interface Palette {
    // neutral: Palette['primary'];
    mutes: {
      light: string
      mid: string
    }
    subjects: {
      act: Pick<
        Subjects,
        "math" | "science" | "english" | "reading" | "overall"
      >
      sat: Pick<Subjects, "math" | "writingandlanguage" | "reading" | "overall">
      psat: Pick<
        Subjects,
        "math" | "writingandlanguage" | "reading" | "overall"
      >
      dsat: Pick<
        Subjects,
        "math" | "readingandwriting" | "overall" | "mathematics"
      >
      staar: Pick<
        Subjects,
        | "algebra"
        | "algebrai"
        | "algebraii"
        | "biology"
        | "english"
        | "englishi"
        | "englishii"
        | "englishiii"
        | "ushistory"
        | "overall"
      >
      tsia2: Pick<Subjects, "math" | "elar">
    }
  }
  interface PaletteOptions {
    // neutral: PaletteOptions['primary'];
    mutes: {
      light: string
      mid: string
    }
    subjects: {
      act: Pick<
        Subjects,
        "math" | "science" | "english" | "reading" | "overall"
      >
      psat: Pick<
        Subjects,
        "math" | "writingandlanguage" | "reading" | "overall"
      >
      sat: Pick<Subjects, "math" | "writingandlanguage" | "reading" | "overall">
      dsat: Pick<
        Subjects,
        "math" | "readingandwriting" | "overall" | "mathematics"
      >
      staar: Pick<
        Subjects,
        | "algebra"
        | "algebrai"
        | "algebraii"
        | "biology"
        | "english"
        | "englishi"
        | "englishii"
        | "englishiii"
        | "ushistory"
        | "overall"
      >
      tsia2: Pick<Subjects, "math" | "elar">
    }
  }
}

const defaultPrimaryColor = blueGrey
const defaultSecondaryColor = lightBlue

export const ManagementThemeDesktopFonts = {
  root: {
    fontSize: "1rem"
  },
  h1: {
    fontSize: "1.5rem",
    lineHeight: "1.75rem"
  },
  h2: {
    fontSize: "1.3125rem",
    lineHeight: "1.5rem"
  },
  h3: {
    fontSize: "1.25rem",
    lineHeight: "1.375rem"
  },
  h4: {
    fontSize: "1.125rem",
    lineHeight: "1.25rem"
  },
  h5: {
    fontSize: "1rem",
    lineHeight: "1.125rem"
  },
  h6: {
    fontSize: "0.875rem",
    lineHeight: "1rem"
  },
  subtitle1: {
    fontSize: "0.75rem",
    lineHeight: "1rem"
  },
  subtitle2: {
    fontSize: "0.6875rem",
    lineHeight: "0.875rem"
  },
  body1: {
    fontSize: "1rem",
    lineHeight: "1.375rem"
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: "1.125rem"
  },
  button: {
    fontSize: "0.875rem"
  },
  get citation() {
    return this.body2
  },
  caption: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem"
  },
  overline: {
    fontSize: "0.6875rem",
    lineHeight: "0.875rem"
  },
  iconsmall: {
    fontSize: "1rem"
  },
  iconlarge: {
    fontSize: "2.25rem"
  },
  input: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem"
  }
}

export const ManagementThemeMobileFonts = {
  root: {
    fontSize: "0.875rem",
    fontWeight: 500
  },
  h1: {
    fontSize: "1.125rem",
    lineHeight: "1.25rem",
    fontWeight: 500
  },
  h2: {
    fontSize: "1.0625rem",
    lineHeight: "1.1875rem",
    fontWeight: 500
  },
  h3: {
    fontSize: "1rem",
    lineHeight: "1.125rem",
    fontWeight: 500
  },
  h4: {
    fontSize: "0.9375rem",
    lineHeight: "1.0625rem",
    fontWeight: 500
  },
  h5: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    fontWeight: 500
  },
  h6: {
    fontSize: "0.8125rem",
    lineHeight: "1rem",
    fontWeight: 500
  },
  subtitle1: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem",
    fontWeight: 500
  },
  subtitle2: {
    fontSize: "0.6875rem",
    lineHeight: "0.875rem",
    fontWeight: 400
  },
  body1: {
    fontSize: "0.875rem",
    lineHeight: "1.125rem",
    fontWeight: 400
  },
  body2: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem",
    fontWeight: 400
  },
  button: {
    fontSize: "0.8125rem",
    fontWeight: 500
  },
  get citation() {
    return this.body2
  },
  caption: {
    fontSize: "0.6875rem",
    lineHeight: "0.875rem",
    fontWeight: 400
  },
  overline: {
    textTransform: "none",
    fontSize: "0.6875rem",
    lineHeight: "0.875rem",
    fontWeight: 500
  },
  iconsmall: {
    fontSize: "0.875rem"
  },
  iconlarge: {
    fontSize: "2.25rem"
  },
  input: {
    fontSize: "0.75rem",
    fontWeight: 400
  }
}

// APP Breakpoints
const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  }
})

export const ManagementTheme = createTheme({
  breakpoints: breakpoints,
  mixins: {
    focusRing,
    focusRingOnDark,
    focusRingTwoTone
  },
  shape: {
    borderRadius: 4, // MUI's original borderRadius size (let's not change without extensive testing)
    // Custom border Radius sizes
    borderRadiusSm: 3,
    borderRadiusMd: 6,
    borderRadiusLg: 12
  },
  palette: {
    primary: defaultPrimaryColor,
    secondary: {
      ...defaultSecondaryColor,
      main: defaultSecondaryColor[600]
    },
    error: red,
    success: lightGreen,
    info: indigo,
    warning: orange,
    grey: teal,
    text: {
      primary: blueGrey[800],
      secondary: lightBlue[600],
      disabled: blueGrey[500]
    },
    link: {
      main: lightBlue[700],
      hover: lightBlue[800],
      disabled: lightBlue[300]
    },
    status: {
      active: "#FFFFFF",
      inactive: blueGrey[100]
    },
    focus: FOCUS_COLOR,
    focusOnDark: FOCUS_COLOR_ON_DARK,
    mutes: {
      light: blueGrey[50],
      mid: blueGrey[200]
    },
    subjects: {
      act: {
        math: {
          primary: red[600],
          secondary: red[500]
        },
        science: {
          primary: lightGreen[900],
          secondary: lightGreen[700]
        },
        english: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        reading: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        },
        overall: {
          primary: teal[600],
          secondary: teal[400]
        }
      },
      sat: {
        overall: {
          primary: teal[600],
          secondary: teal[400]
        },
        math: {
          primary: red[600],
          secondary: red[500]
        },
        writingandlanguage: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        reading: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        }
      },
      psat: {
        overall: {
          primary: teal[600],
          secondary: teal[400]
        },
        math: {
          primary: red[600],
          secondary: red[500]
        },
        writingandlanguage: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        reading: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        }
      },
      dsat: {
        overall: {
          primary: teal[600],
          secondary: teal[400]
        },
        mathematics: {
          primary: red[600],
          secondary: red[500]
        },
        math: {
          primary: red[600],
          secondary: red[500]
        },
        readingandwriting: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        }
      },
      staar: {
        overall: {
          primary: teal[600],
          secondary: teal[400]
        },
        algebra: {
          primary: red[600],
          secondary: red[500]
        },
        algebrai: {
          primary: red[600],
          secondary: red[500]
        },
        algebraii: {
          primary: red[600],
          secondary: red[500]
        },
        biology: {
          primary: lightGreen[900],
          secondary: lightGreen[700]
        },
        english: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        englishi: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        englishii: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        englishiii: {
          primary: indigo[600],
          secondary: indigo[500]
        },
        ushistory: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        }
      },
      tsia2: {
        elar: {
          primary: lightBlue[700],
          secondary: lightBlue[500]
        },
        math: {
          primary: red[600],
          secondary: red[500]
        }
      }
    }
  },
  spacing: [0, 4, 8, 12, 16, 24, 32, 48, 64, 80, 124],
  typography: {
    fontFamily: "Effra",
    h1: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h1,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h1
      }
    },
    h2: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h2,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h2
      }
    },
    h3: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h3,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h3
      }
    },
    h4: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h4,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h4
      }
    },
    h5: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h5,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h5
      }
    },
    h6: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.h6,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.h6
      }
    },
    subtitle1: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.subtitle1,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.subtitle1
      }
    },
    subtitle2: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.subtitle2,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.subtitle2
      }
    },
    body1: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.body1,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.body1
      }
    },
    body2: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.body2,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.body2
      }
    },
    button: {
      fontFamily: "Effra",
      textTransform: "none",
      ...ManagementThemeMobileFonts.button,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.button
      }
    },
    citation: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.citation,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.citation
      }
    },
    // @ts-ignore
    overline: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.overline,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.overline
      }
    },
    caption: {
      fontFamily: "Effra",
      ...ManagementThemeMobileFonts.caption,
      [breakpoints.up("md")]: {
        ...ManagementThemeDesktopFonts.caption
      }
    }
  },
  shadows: [
    "none",
    "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
    "0 1px 1px -2px rgba(0,0,0,0.20), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)",
    "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
    "0 2px 4px -1px rgba(0,0,0,0.20), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
    "0 3px 5px -1px rgba(0,0,0,0.20), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)",
    "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
    "0 5px 5px -3px rgba(0,0,0,0.20), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12)",
    "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
    "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
    "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
    "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
    "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
    "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
    "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
    "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
    "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
    "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
    "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
    "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)"
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          color: "white"
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        // root: {
        //   ...mobileFonts.root,
        //   [breakpoints.up("md")]: {
        //     ...desktopFonts.root
        //   }
        // }
      },
      variants: [
        {
          props: { variant: "citation" },
          style: {}
        },
        {
          props: { color: "muted" },
          style: { color: blueGrey[500] }
        },
        {
          props: { color: "link" },
          style: { color: lightBlue[700] }
        }
      ]
    },
    MuiLink: {
      defaultProps: {
        underline: "hover"
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.link.main,
          "&:hover": { color: theme.palette.link.hover },
          "&:focus-visible": { color: theme.palette.link.hover },
          "&[aria-disabled='true']": {
            color: theme.palette.link.disabled,
            pointerEvents: "none"
          }
        })
      }
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          overflow: "visible"
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          ...ManagementThemeMobileFonts.iconsmall,
          [breakpoints.up("md")]: {
            ...ManagementThemeDesktopFonts.iconsmall
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          ...ManagementThemeMobileFonts.input,
          [breakpoints.up("md")]: {
            ...ManagementThemeDesktopFonts.input
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(blueGrey[900], 0.65),
          margin: "8px 0px",
          fontWeight: 400,
          fontSize: "0.75rem",
          "&:first-letter": {
            textTransform: "uppercase"
          },

          [`.${tooltipClasses.popper}[data-popper-placement*="bottom"] &`]: {
            marginTop: "4px"
          }
        },
        tooltipArrow: {
          backgroundColor: alpha(blueGrey[900], 0.75)
        },
        arrow: {
          color: alpha(blueGrey[900], 0.75)
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        ["button,a,div,p,h1,h2,h3,h4,h5,h6,img,circle,ul,th"]: {
          "&:focus": focusRing.ring,
          "&:focus-visible": focusRing.ring
        },
        ".navbar-contained": {
          width: "100vw",
          marginLeft: `min(calc((${breakpoints.values["xl"]}px - 100vw) / 2) , 0px)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          "& > *": {
            width: "100%",
            maxWidth: breakpoints.values["xl"],
            margin: "0 auto"
          }
        },
        body: {
          backgroundColor: defaultPrimaryColor["50"]
        }
      }
    },
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          "& .katex .mfrac .frac-line": {
            // Default katex values for fraction line.
            borderBottomWidth: "0.04em",
            height: "0.04em"
          }
        }
      }
    }
  }
})
